import { routesMap } from 'app/Router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import jobService from 'services/jobService';
import { parseCanvaPublishDraftId } from 'utils/canvaPublishDraft';
import {
  gallerySkuFilterSearch,
  parseGallerySkuFilter,
} from 'utils/gallerySkuFilter';
import {
  getCanvaSessionSkuFilter,
  markCanvaSession,
} from 'utils/canvaSession';

/**
 * Id of the Canva design parked for this gallery visit, from {@code ?canvaDraftId=}. Undefined on
 * every normal gallery visit, which is what keeps the existing "pick a SKU, start a job config"
 * flow untouched.
 */
export const useCanvaPublishDraftId = (): string | undefined => {
  const { search } = useLocation();
  const canvaDraftId = useMemo(() => parseCanvaPublishDraftId(search), [search]);

  // The other entry point besides the SSO exchange: an already-authenticated buyer following the
  // hand-off link. Marked here, where the id is read, because it disappears from the URL as soon
  // as the buyer opens a product and the shop still has to treat them as a Canva buyer.
  useEffect(() => {
    if (canvaDraftId) {
      // The id itself is remembered, not just the fact of the session: entry points into a
      // product that drop the query string would otherwise lose the design. The products the
      // hand-off restricted the visit to travel with it, so "choose a different product"
      // returns to the same restricted set. See canvaSession.
      markCanvaSession(
        canvaDraftId,
        gallerySkuFilterSearch(parseGallerySkuFilter(search))
      );
    }
  }, [canvaDraftId, search]);

  return canvaDraftId;
};

/**
 * Gallery pick for a visit that came from Canva: publish the parked artwork against the chosen SKU
 * and land on the same expert job config the direct Canva publish creates.
 */
export const useCanvaPublishDraft = () => {
  const canvaDraftId = useCanvaPublishDraftId();
  const history = useHistory();
  const [publishing, setPublishing] = useState(false);

  const publishWithSku = useCallback(
    async (skuId: number) => {
      if (!canvaDraftId) {
        return;
      }
      setPublishing(true);
      try {
        const { jobConfigId } = await jobService.publishCanvaDraft({
          draftId: canvaDraftId,
          skuId,
          // A hand-off that named the products has nothing for size matching to decide, so the
          // job must not be started with a preselected size, an exposed size section or a
          // "does not fit" warning. Read from the session rather than the URL: the pick can
          // happen from a page the tags have already dropped off.
          skuFromNamedSet: Boolean(getCanvaSessionSkuFilter()),
        });
        // Kept, not consumed: the buyer may have picked the wrong product, and "choose a
        // different product" has to put the same design on another one rather than start them
        // on an empty job. The server keeps the draft for its TTL too. It is released when the
        // job reaches the cart -- see jobConfigModel's cart effects.
        history.push(`${routesMap.jobConfig.path}/expert/${jobConfigId}`);
      } finally {
        setPublishing(false);
      }
    },
    [canvaDraftId, history]
  );

  return { canvaDraftId, isCanvaPublish: Boolean(canvaDraftId), publishWithSku, publishing };
};
